import {useState, useCallback, useEffect, useMemo} from 'react';
import produce from 'immer';
import get from 'lodash/get';
import {useSMCSnackbar} from '../../../utils/hooks';
import {useTranslation} from 'react-i18next';
import {setProperty, getErrorCode, isStringI18n, extractI18nString} from '../../../utils/utils';
import Api from '../../../api/SMPFetchHelper';
import {ObjectTypes, ApiErrorCodes, ErrorCodes} from '../../../api/consts';
import {useSMCErrorHandler} from '../../../utils/errorUtils';
import store from '../../../store';
import {push} from 'connected-react-router';
import {SMCPaths} from '../../../conf/Routes';
import {FirmwareGetSchema} from '../../../api/DBSchema/Firmware';
import generalIcon from '../../../assets/images/general.png';
import domainIcon from '../../../assets/images/domain.png';
import historyIcon from '../../../assets/images/history.png';
import FirmwareGeneral from './InnerPages/FirmwareGeneral';
import FirmwareHistory from './InnerPages/FirmwareHistory';
import FirmwareServiceDomains from './InnerPages/FirmwareServiceDomains';

export function useFirmwareEditor(firmwareId, defaultChosenPageId) {
    const { t } = useTranslation();
    const { enqueueSuccessSnackbar, enqueueErrorSnackbar, enqueueLogoutSnackbar } = useSMCSnackbar();
    const { getErrorMessage } = useSMCErrorHandler();

    const [origFirmwareObject, setOrigFirmwareObject] = useState({});
    const [firmwareObject, setFirmwareObject] = useState({});

    const [isPageDataLoaded, setIsPageDataLoaded] = useState(false);
    const [isPageLoadFailed, setIsPageLoadFailed] = useState(false);

    const [isObjectDirty, setIsObjectDirty] = useState(false);

    const [chosenPageId, setChosenPageId] = useState(defaultChosenPageId || 'general');
    
    const showServiceDomainPage = useMemo(() => {
        return !!get(firmwareObject, [FirmwareGetSchema.pages.serviceDomains.pageId, FirmwareGetSchema.pages.serviceDomains.visible]);
    }, [firmwareObject]);

    const isPrivateFirnware = useMemo(() => {
        return !!get(firmwareObject, [FirmwareGetSchema.pages.general.pageId, FirmwareGetSchema.pages.general.privateFirmware]);
    }, [firmwareObject]);

    const isRecommendedFirmware = useMemo(() => {
        return !!get(firmwareObject, [FirmwareGetSchema.pages.general.pageId, FirmwareGetSchema.pages.general.recommended]);
    }, [firmwareObject]);

    const editorPages = useMemo(() => [
        { id: FirmwareGetSchema.pages.general.pageId,        title: t('SMP.GENERAL'),             icon: generalIcon, pageElement: FirmwareGeneral,        helpId: ''                               },
        { id: FirmwareGetSchema.pages.serviceDomains.pageId, title: t('SMC_MENU_GLOBAL_PORTALS'), icon: domainIcon,  pageElement: FirmwareServiceDomains, helpId: '', hide: !showServiceDomainPage },
        { id: FirmwareGetSchema.pages.history.pageId,        title: t('SMP.HISTORY'),             icon: historyIcon, pageElement: FirmwareHistory,        helpId: ''                               }
    ], [showServiceDomainPage, t]);

    const pageIdToHelpId = useMemo(() => {
        const _pageIdToHelpId = {};
        const addPagesData = (pagesRoot) => {
            pagesRoot.forEach(page => {
                if (page.id) {
                    _pageIdToHelpId[page.id] = page.helpId;
                }
                if (page.children) {
                    addPagesData(page.children);
                }
            })
        }
        addPagesData(editorPages);
        return _pageIdToHelpId;
    }, [editorPages]);

    const helpId = useMemo(() => pageIdToHelpId[chosenPageId], [pageIdToHelpId, chosenPageId]);

    const fetchFirmware = useCallback(async () => {
        setIsPageDataLoaded(false);
        try {
            const fetchFirmwareResp = await Api.fetchSingleObject(ObjectTypes.FIRMWARE, firmwareId);
            const firmware = fetchFirmwareResp.data.data;
            // Validate server response
            if (!firmware) {
                console.error('Get firmware response failed: firmware response returned empty');
                throw new Error(ErrorCodes.SERVER_ERROR);
            }
            setOrigFirmwareObject(firmware);
            setFirmwareObject(firmware);
            setIsPageLoadFailed(false);
            setIsObjectDirty(false); // Just fetched, not dirty
        } catch (e) {
            const errorCode = getErrorCode(e) || ApiErrorCodes.GENERAL_ERROR;
            if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                enqueueLogoutSnackbar(t(errorCode));
            } else {
                enqueueErrorSnackbar(t(errorCode));
            }
            setIsPageLoadFailed(true);
        }
        setIsPageDataLoaded(true);
    }, [firmwareId, enqueueErrorSnackbar, enqueueLogoutSnackbar, t]);

    const setFirmwareData = useCallback((paramPath, newValue) => {
        setIsObjectDirty(true); // Data changed, object is dirty
        setFirmwareObject(firmwareObj => produce(firmwareObj, draft => {
            setProperty(draft, paramPath, newValue);
        }));
    }, []);

    const onPageChange = useCallback((pageId) => {
        setChosenPageId(pageId);
    }, []);

    const saveButtonOnClick = useCallback(() => {
        Api.updateSingleObject(ObjectTypes.FIRMWARE, firmwareId, firmwareObject)
        .then(() => {
            setIsPageDataLoaded(false);
            fetchFirmware();
            enqueueSuccessSnackbar(t('OBJECT_EDITOR_OBJ_SAVED_OK'));
        })
        .catch((error) => {
            const errorMessage = getErrorMessage(error);
            alert(errorMessage);
        })
        .finally(() => {
            setIsPageDataLoaded(true);
        });
    }, [fetchFirmware, getErrorMessage, firmwareId, firmwareObject, enqueueSuccessSnackbar, t]);

    const changeFirmwareOnAction = useCallback(() => {
        Api.SetFirmwareCustomerHotfixToGA(ObjectTypes.FIRMWARE, firmwareId, firmwareObject)
        .then(() => {
            setIsPageDataLoaded(false);
            fetchFirmware();
            enqueueSuccessSnackbar(t('SMC_FIRMWARE_TYPE_CHANGE_TO_GA'));
        })
        .catch((error) => {
            const errorMessage = getErrorMessage(error);
            alert(errorMessage);
        })
        .finally(() => {
            setIsPageDataLoaded(true);
        });
    }, [fetchFirmware, getErrorMessage, firmwareId, firmwareObject, enqueueSuccessSnackbar, t]);

    const changeFirmwareToRecommendedAction = useCallback(() => {
        Api.SetFirmwareAsRecommended(ObjectTypes.FIRMWARE, firmwareId)
            .then(() => {
                setIsPageDataLoaded(false);
                fetchFirmware();
                enqueueSuccessSnackbar(t('SMC_FIRMWARE_TYPE_CHANGE_TO_RECOMMENDED'));
            })
            .catch((error) => {
                const errorMessage = getErrorMessage(error);
                alert(errorMessage);
            })
            .finally(() => {
                setIsPageDataLoaded(true);
            });
    }, [fetchFirmware, getErrorMessage, firmwareId, enqueueSuccessSnackbar, t]);


    const deleteButtonOnClick = useCallback(() => {
        if (window.confirm(t('OBJECT_EDITOR_OBJ_DELETE', [firmwareId] ))) {
            Api.deleteObjects(ObjectTypes.FIRMWARE, [firmwareId])
            .then(res => {
                const deleteResult = res.data.results[0];
                if (deleteResult.success) {
                    setIsObjectDirty(false); // Set as false to not trigger dirty modal
                    store.dispatch(push(SMCPaths.firmwares));
                    enqueueSuccessSnackbar(t('OBJECT_EDITOR_OBJ_DELETE_OK', [firmwareId]));
                } else {
                    const deleteErrorCode = deleteResult.errorCode;
                    let deleteErrorParams;
                    if (deleteResult.errorParams) {
                        deleteErrorParams = deleteResult.errorParams.map(param => (
                            isStringI18n(param)) ? t(extractI18nString(param)) : param
                        );
                    }
                    enqueueErrorSnackbar(t(extractI18nString(deleteErrorCode), deleteErrorParams));
                }
            })
            .catch(err => {
                const errorCode = getErrorMessage(err);
                enqueueErrorSnackbar(t(errorCode));
            });
        }
    }, [firmwareId, getErrorMessage, enqueueSuccessSnackbar, enqueueErrorSnackbar, t]);

    const revertButtonOnClick = useCallback(() => {
        setFirmwareObject(origFirmwareObject);
        setIsObjectDirty(false); // Is not dirty, object is reset to originl values
    }, [origFirmwareObject]);

    // Fetch firmware on loading of page
    useEffect(() => {
        fetchFirmware();
    }, [fetchFirmware]);
    
    return {
        firmwareObject,
        isObjectDirty,
        editorPages,
        helpId,
        isPageDataLoaded,
        isPageLoadFailed,
        chosenPageId,
        onPageChange,
        setFirmwareData,
        saveButtonOnClick,
        deleteButtonOnClick,
        revertButtonOnClick,
        changeFirmwareOnAction,
        isPrivateFirnware,
        changeFirmwareToRecommendedAction,
        isRecommendedFirmware,

        reloadPageData: fetchFirmware
    };
}